import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Loader2, AlertCircle, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

interface LoginForm {
  email: string;
  password: string;
}

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationNeeded, setVerificationNeeded] = useState<string | null>(null);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setVerificationNeeded(null);
    
    try {
      const response = await api.post<{
        user: any;
        accessToken: string;
        refreshToken: string;
      }>('/auth/login', data);

      login(response.user, response.accessToken, response.refreshToken);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error: any) {
      const errorData = error.data || error.response?.data;
      
      if (errorData?.requiresVerification) {
        setVerificationNeeded(errorData.email);
      } else {
        const message = errorData?.message || 'Invalid credentials';
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerification = async () => {
    if (!verificationNeeded) return;
    
    setIsResending(true);
    try {
      await api.post('/auth/resend-verification', { email: verificationNeeded });
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Logo - mobile only */}
      <div className="lg:hidden text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#6B9F54] flex items-center justify-center shadow-sm">
            <span className="text-xl font-bold text-white">GG</span>
          </div>
          <span className="text-xl font-bold text-slate-900">Green Gold</span>
        </Link>
      </div>

      {/* Header */}
      <div className="text-center lg:text-left">
        <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">Welcome back</h2>
        <p className="text-slate-600">
          Sign in to access your business suite
        </p>
      </div>

      {/* Verification needed message */}
      {verificationNeeded && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-amber-50 border border-amber-300 shadow-sm"
        >
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <AlertCircle className="text-amber-600 flex-shrink-0" size={16} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-1">Email Verification Required</h3>
              <p className="text-sm text-amber-700">
                Please verify your email address before logging in. Check your inbox for a verification link.
              </p>
              <button
                onClick={resendVerification}
                disabled={isResending}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isResending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Resend Verification Email
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Mail size={18} />
            </div>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className={`w-full pl-11 pr-4 py-2.5 bg-white border rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors.email 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-[#6B9F54] focus:border-[#6B9F54]'
              }`}
              placeholder="you@example.com"
            />
          </div>
          {errors.email && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-sm text-red-600"
            >
              {errors.email.message}
            </motion.p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-medium text-slate-700">Password</label>
            <Link 
              to="/forgot-password" 
              className="text-xs text-[#6B9F54] hover:text-[#5A8C43] font-medium transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Lock size={18} />
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
              })}
              className={`w-full pl-11 pr-11 py-2.5 bg-white border rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                errors.password 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-[#6B9F54] focus:border-[#6B9F54]'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1.5 text-sm text-red-600"
            >
              {errors.password.message}
            </motion.p>
          )}
        </div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className="w-full py-3 px-4 bg-[#6B9F54] hover:bg-[#5A8C43] text-white rounded-lg font-semibold text-base transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
        >
          {isLoading ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            <>
              Sign In
              <ArrowRight size={18} />
            </>
          )}
        </motion.button>
      </form>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-[#F5F1E8] text-slate-500">New to Green Gold?</span>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center">
        <Link
          to="/register"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#6B9F54] hover:text-[#5A8C43] transition-colors"
        >
          Create an account
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
